import { ArrowUpOutlined } from '@ant-design/icons';
import collect from 'collect.js';
import React, { useMemo } from 'react';
import { abs } from 'mathjs';
import { Line } from 'react-chartjs-2';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from 'chart.js';
import moment from 'moment';
import { Empty } from 'antd';
import useHelpers from '../../hooks/context/useHelpers';
import HelperMethods from '../../services/exports/HelperMethods';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Tooltip, Legend, Filler);

export default function LineChartCard({ metric, title, data, hasTimeLine = true }) {
  const { formatCurrency } = useHelpers();

  const chartData = {
    labels: data?.date_ranges ?? [],
    datasets: [
      {
        label: title,
        data: collect(data?.date_ranges)
          .map((item) => (item.value?.amount ? Number(item.value.amount) / 100 : item.value))
          .toArray(),
        borderWidth: 2,
        borderColor: 'rgba(105, 206, 140, 1)',
        pointRadius: 1,
        pointHoverRadius: 2,
        fill: 'start',
        tension: 0,
        backgroundColor: (context) => {
          const { ctx } = context.chart;
          const gradient = ctx.createLinearGradient(0, 0, 0, 200); // Top to bottom
          gradient.addColorStop(0, 'rgba(255, 255, 255, 0)'); // Transparent at the top
          gradient.addColorStop(1, 'rgba(105, 206, 140, 1)'); // Solid green at the bottom

          return gradient;
        },
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title(tooltipItems) {
            const index = tooltipItems[0].dataIndex;
            const range = data?.date_ranges[index]?.period;

            return `${moment(range.start).format('DD MMM YYYY')} - ${moment(range.end).format('DD MMM YYYY')}`;
          },
          label(tooltipItem) {
            return tooltipItem.raw;
          },
        },
      },
    },
    elements: {
      line: {
        borderWidth: 2,
      },
      point: {
        hitRadius: 10,
      },
    },
    scales: {
      x: {
        display: true,
        ticks: {
          display: false,
        },
        grid: {
          display: true,
        },
      },
      y: {
        display: true,
        grid: {
          display: true,
        },
      },
    },
  };

  const formatValue = (value) => {
    switch (metric) {
      case 'average_open_rate':
      case 'average_click_rate':
      case 'google_ads_absolute_top_impression_percentage':
        return `${abs((value ?? 0) * 100).toFixed(0)}%`;
      case 'google_ads_conversions_from_interactions_rate':
        return HelperMethods.formatPercentage(HelperMethods.round((value ?? 0) * 100));
      case 'google_ads_average_cpc':
        return formatCurrency(value ?? 0);
      default:
        return value;
    }
  };

  const formatChange = (value) => `${abs(value ?? 0).toFixed(2)}%`;

  const renderValue = (field) => {
    if (!data) {
      return 0;
    }

    if (data[field]?.formatted) {
      return data[field].formatted;
    }

    return data[field];
  };

  const renderChange = useMemo(() => {
    if (!hasTimeLine) {
      return;
    }

    function getChange() {
      if (!data) {
        return;
      }

      if (Array.isArray(data.change)) {
        return collect(data.change).reduce(
          (result, item) => (result !== '' ? `${result} / ${formatChange(item)}` : formatChange(item)),
          '',
        );
      }

      return formatChange(data.change);
    }

    if (!data) {
      return null;
    }

    if (data?.change < 0) {
      return null;
    }

    if (data?.change > 0) {
      return (
        <p className="text-brand-success flex-none mini">
          <ArrowUpOutlined className="my-auto mr-1" />
          {getChange()}
        </p>
      );
    }

    return null;
  }, [data?.change, hasTimeLine]);

  function renderTimeLine() {
    if (!hasTimeLine) {
      return null;
    }

    if (collect(data?.date_ranges).count() > 0) {
      return (
        <div className="p-xs">
          <Line data={chartData} options={chartOptions} />
        </div>
      );
    }

    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  return (
    <div className="bg-white border border-brand-inkGrey_grey_7 rounded-xl overflow-hidden hover:shadow cursor-pointer">
      <div className="w-full p-md">
        <div className="gap-4 items-start">
          <h6 className="mini semibold">{title}</h6>
          <div className="flex gap-2 mt-xs items-center">
            <h6>{formatValue(renderValue('value'))}</h6>
            {renderChange}
          </div>
        </div>
      </div>
      {renderTimeLine()}
    </div>
  );
}
